import React, { useState, useMemo, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { FiFilter, FiX } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { ApplicantsFilters } from './ApplicantsFilters';
import type { ApplicantStatusFilter } from './ApplicantsFilters';
import { ApplicantCard } from './ApplicantCard';
import { ApplicantDetailPanel } from './ApplicantDetailPanel';
import { EmptyState } from '../../../ui/EmptyState';
import type { DateRange } from '../common/DateRangePicker';
import { isOfferEffectivelyExpired, type ApplicantItem, type OfferItem } from '../types';
import { AcceptApplicationModal } from '../../AcceptApplicationModal';
import { RejectApplicationModal } from '../../RejectApplicationModal';
import {
  useGetGigApplications,
  useGetGigApplication,
  useGetGigs,
  type GigApplicationListItem,
  type GigApplicationsFilterParams,
} from '../../../../api/queries';
import { useGetPublicProfile } from '../../../../api/profile';
import { useUpdateGigApplication } from '../../../../api/mutations';
import { mapApiOfferToItem, useOffersList, type ApiOfferDetail } from '../../../../api/offers';
import { apiClient } from '../../../../api/api';
import { useAuth } from '../../../../hooks/useAuth';
import { useViewportFitHeight } from '../../../../hooks/useViewportFitHeight';
import { toast } from '../../../../utils/toast';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimeAgo(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function normalizeStringArray(raw: unknown): string[] {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: unknown) => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        if (typeof obj.name === 'string') return obj.name;
        if (typeof obj.title === 'string') return obj.title;
      }
      return '';
    })
    .filter(Boolean);
}

function buildLocation(
  profile: Record<string, unknown> | null | undefined,
  gig?: { city?: string; state?: string; country?: string },
): string {
  const city = typeof profile?.city === 'string' ? profile.city : undefined;
  const state = typeof profile?.state === 'string' ? profile.state : undefined;
  const country = typeof profile?.country === 'string' ? profile.country : undefined;
  const parts = [city, state, country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return [gig?.city, gig?.state, gig?.country].filter(Boolean).join(', ') || '';
}

function extractSocialLinks(profile: Record<string, unknown> | null | undefined) {
  const links = (profile?.visible_social_links ?? profile?.social_links ?? profile) as
    | Record<string, string>
    | undefined;
  return {
    spotify: links?.spotify,
    appleMusic: links?.apple_music ?? links?.appleMusic,
    tiktok: links?.tiktok,
    website: links?.website,
    instagram: links?.instagram,
    twitter: links?.twitter,
  };
}

function composeEnriched(applicationDetail: any, publicProfile: any): ApplicantItem | null {
  if (!applicationDetail) return null;
  const d = applicationDetail as any;
  const p = publicProfile as any;
  return {
    application_uuid: d.application_uuid,
    status: d.status,
    fullName: d.applicant?.full_name ?? d.applicant?.username ?? '',
    username: d.applicant?.username ?? '',
    email: d.applicant_email ?? d.applicant?.email ?? '',
    profileImage: p?.prof_pics ?? p?.profile_image ?? p?.profPics ?? null,
    bio: p?.bio ?? '',
    genres: normalizeStringArray(p?.genres),
    userTypes: normalizeStringArray(p?.user_types),
    location: buildLocation(p, d.gig),
    timeAgo: formatTimeAgo(d.created_at),
    appliedToGig: d.gig?.title ?? '',
    gigDescription: d.gig?.description ?? '',
    gigUuid:
      d.gig?.gig_uuid != null
        ? String(d.gig.gig_uuid).trim() || null
        : d.gig?.id != null
          ? String(d.gig.id).trim() || null
          : null,
    applicantUserId: (() => {
      const raw =
        d.applicant?.id ??
        (d as Record<string, unknown>)?.applicant_id ??
        (d as Record<string, unknown>)?.applicant_user_id ??
        (d as Record<string, unknown>)?.user_id;
      return raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    })(),
    skills: normalizeStringArray(p?.skills),
    socialLinks: extractSocialLinks(p),
  };
}

// ─── Skeletons ──────────────────────────────────────────────────────────────

const CardSkeleton: React.FC = () => (
  <div className="rounded-2xl border border-white/10 bg-[#090a0e] p-4 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-full bg-white/5" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 rounded bg-white/5" />
        <div className="h-3 w-24 rounded bg-white/5" />
      </div>
    </div>
    <div className="mt-3 h-10 rounded-xl bg-white/5" />
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

export const ApplicantsTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Filters state
  const [statusFilter, setStatusFilter] = useState<ApplicantStatusFilter>('All');
  const [dateRange, setDateRange] = useState<DateRange>({ preset: 'Any date' });
  const [gigFilter, setGigFilter] = useState('All');
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>();
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Modal states
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const [detailPanelRef, detailPanelHeight] = useViewportFitHeight<HTMLDivElement>({
    bottomGap: 28,
    min: 320,
  });

  // ── Queries ───────────────────────────────────────────────────────────────

  const apiParams = useMemo((): GigApplicationsFilterParams => ({ page: 1, page_size: 1000 }), []);

  const {
    data: applicationsData,
    isLoading: listLoading,
    isError: listError,
  } = useGetGigApplications(apiParams);

  const { data: myGigsData } = useGetGigs({ user: user?.id, page_size: 100 });

  const { data: applicationDetail, isLoading: detailLoading } = useGetGigApplication(selectedUuid);
  const applicantId = applicationDetail?.applicant?.id;
  const { data: publicProfile, isLoading: profileLoading } = useGetPublicProfile(applicantId);

  const updateApplication = useUpdateGigApplication();

  const { data: createdOffersData } = useOffersList({ perspective: 'created', page_size: 100 });

  // ── Data Processing ───────────────────────────────────────────────────────

  const rawApplications = useMemo(() => applicationsData?.data?.results ?? [], [applicationsData]);

  const availableGigs = useMemo(() => {
    const gigs: any[] = myGigsData?.results ?? [];
    const titles = gigs.map((g) => g.title).filter(Boolean);
    if (titles.length > 0) return titles;
    const appTitles = Array.from(new Set(rawApplications.map((a) => a.gig.title).filter(Boolean)));
    return appTitles;
  }, [myGigsData, rawApplications]);

  const mappedApplications = useMemo((): ApplicantItem[] => {
    return rawApplications.map((app: GigApplicationListItem) => ({
      application_uuid: app.application_uuid,
      status: app.status,
      fullName: app.applicant.full_name || app.applicant.username,
      username: app.applicant.username,
      email: '',
      profileImage: null,
      userTypes: [],
      location: [app.gig.city, app.gig.country].filter(Boolean).join(', '),
      timeAgo: formatTimeAgo(app.created_at),
      appliedToGig: app.gig.title,
      gigUuid: app.gig.gig_uuid != null ? String(app.gig.gig_uuid).trim() || null : null,
      applicantUserId:
        app.applicant?.id != null && !Number.isNaN(Number(app.applicant.id))
          ? Number(app.applicant.id)
          : null,
      bio: '',
      genres: [],
      skills: [],
      socialLinks: {},
    }));
  }, [rawApplications]);

  const counts = useMemo(() => {
    const apiCounts = applicationsData?.counts;
    if (apiCounts) return apiCounts;
    return {
      all: mappedApplications.length,
      pending: mappedApplications.filter((a) => a.status === 'pending').length,
      accepted: mappedApplications.filter((a) => a.status === 'accepted').length,
      rejected: mappedApplications.filter((a) => a.status === 'rejected').length,
    };
  }, [applicationsData, mappedApplications]);

  const filteredApplicants = useMemo(() => {
    return mappedApplications.filter((app) => {
      if (statusFilter !== 'All' && app.status !== statusFilter) return false;
      if (gigFilter !== 'All' && app.appliedToGig !== gigFilter) return false;
      return true;
    });
  }, [mappedApplications, statusFilter, gigFilter]);

  // Sync selection with filtered results
  useEffect(() => {
    if (!selectedUuid && filteredApplicants.length > 0) {
      setSelectedUuid(filteredApplicants[0].application_uuid);
    }
    if (selectedUuid && !filteredApplicants.some((a) => a.application_uuid === selectedUuid)) {
      setSelectedUuid(filteredApplicants[0]?.application_uuid);
    }
  }, [filteredApplicants, selectedUuid]);

  // Escape key listener to deselect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedUuid(undefined);
        setShowMobileSheet(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const selectedApplicant = useMemo(
    () => composeEnriched(applicationDetail, publicProfile),
    [applicationDetail, publicProfile],
  );

  // The offers list serializer omits gig_uuid, recipient ids and dates, so a
  // gig/recipient match is impossible from list data alone. Narrow to offers
  // addressed to this applicant's username, then resolve the gig check against
  // fetched offer details (which do carry gig_uuid and expiration_date).
  const MAX_OFFER_DETAIL_LOOKUPS = 10;

  const selectedUsername = selectedApplicant?.username?.trim().toLowerCase();

  const candidateOfferUuids = useMemo(() => {
    if (!selectedApplicant || selectedApplicant.status !== 'accepted' || !selectedUsername) {
      return [];
    }
    const seen = new Set<string>();
    const uuids: string[] = [];
    for (const offer of createdOffersData?.results ?? []) {
      if (offer.status === 'withdrawn' || offer.status === 'rejected') continue;
      const recipientUsername = offer.recipientUsername?.trim().toLowerCase();
      if (!recipientUsername || recipientUsername !== selectedUsername) continue;
      if (!offer.id || seen.has(offer.id)) continue;
      seen.add(offer.id);
      uuids.push(offer.id);
      if (uuids.length >= MAX_OFFER_DETAIL_LOOKUPS) break;
    }
    return uuids;
  }, [selectedApplicant, selectedUsername, createdOffersData]);

  const offerDetailQueries = useQueries({
    queries: candidateOfferUuids.map((offerUuid) => ({
      queryKey: ['offers', offerUuid],
      staleTime: 30_000,
      queryFn: async () => {
        const { data } = await apiClient.get(`/offers/${offerUuid}/`);
        return mapApiOfferToItem(data as ApiOfferDetail);
      },
    })),
  });

  const detailedOffers = useMemo(
    () => offerDetailQueries.map((query) => query.data).filter((offer) => offer != null),
    [offerDetailQueries],
  );

  const existingOfferId = useMemo(() => {
    if (!selectedApplicant || selectedApplicant.status !== 'accepted') return undefined;
    const gigUuid =
      selectedApplicant.gigUuid != null ? String(selectedApplicant.gigUuid).trim() : '';
    if (!gigUuid) return undefined;
    const applicantUserId =
      selectedApplicant.applicantUserId != null &&
      !Number.isNaN(Number(selectedApplicant.applicantUserId))
        ? Number(selectedApplicant.applicantUserId)
        : null;
    const email = selectedApplicant.email?.trim().toLowerCase();
    // Detail results win over list results: only details carry the real
    // gigUuid and expiration date.
    const byId = new Map<string, OfferItem>();
    for (const offer of createdOffersData?.results ?? []) {
      if (offer.id && !byId.has(offer.id)) byId.set(offer.id, offer);
    }
    for (const offer of detailedOffers) {
      byId.set(offer.id, offer);
    }
    const gigOffers = [...byId.values()].filter((offer) => {
      const offerGigUuid = offer.gigUuid != null ? String(offer.gigUuid).trim() : '';
      if (offerGigUuid !== gigUuid) return false;
      if (offer.status === 'withdrawn' || offer.status === 'rejected') return false;
      if (isOfferEffectivelyExpired(offer)) return false;
      return true;
    });
    if (gigOffers.length === 0) return undefined;
    const mine = gigOffers.filter((offer) => {
      const offerUserId =
        offer.recipientUserId != null && !Number.isNaN(Number(offer.recipientUserId))
          ? Number(offer.recipientUserId)
          : null;
      const offerEmail = offer.recipientEmail?.trim().toLowerCase();
      if (applicantUserId != null && offerUserId != null && offerUserId === applicantUserId) {
        return true;
      }
      if (email && offerEmail && offerEmail === email) return true;
      return false;
    });
    const pool = mine.length > 0 ? mine : gigOffers;
    pool.sort((a, b) => {
      const aTime = new Date(a.sentAt ?? a.createdAt ?? 0).getTime();
      const bTime = new Date(b.sentAt ?? b.createdAt ?? 0).getTime();
      if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
        return bTime - aTime;
      }
      return 0;
    });
    return pool[0]?.id;
  }, [selectedApplicant, createdOffersData, detailedOffers]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleAcceptConfirm = () => {
    if (!selectedUuid || !selectedApplicant) return;
    const recipientUserId = selectedApplicant.applicantUserId ?? undefined;
    const recipientEmail = selectedApplicant.email || undefined;
    if (!recipientUserId && !recipientEmail) {
      toast.error('Unable to create offer. Applicant contact details are missing.');
      setShowAcceptModal(false);
      return;
    }
    if (!selectedApplicant.application_uuid) {
      toast.error('Unable to create offer without a gig application.');
      setShowAcceptModal(false);
      return;
    }
    updateApplication.mutate(
      {
        applicationUuid: selectedUuid,
        status: 'accepted',
        notifyApplicant: false,
      },
      {
        onSuccess: () => {
          setShowAcceptModal(false);
          openCreateOffer(selectedApplicant);
        },
      },
    );
  };

  const handleRejectConfirm = (notifyApplicant: boolean) => {
    if (!selectedUuid) return;
    updateApplication.mutate({
      applicationUuid: selectedUuid,
      status: 'rejected',
      notifyApplicant,
    });
    setShowRejectModal(false);
  };

  const openCreateOffer = (applicant?: ApplicantItem | null) => {
    const fromArg = applicant;
    const target =
      fromArg &&
      selectedApplicant &&
      fromArg.application_uuid === selectedApplicant.application_uuid
        ? {
            ...fromArg,
            ...selectedApplicant,
            email: selectedApplicant.email || fromArg.email,
            gigDescription: selectedApplicant.gigDescription || fromArg.gigDescription,
            gigUuid: selectedApplicant.gigUuid || fromArg.gigUuid,
            applicantUserId: selectedApplicant.applicantUserId ?? fromArg.applicantUserId,
          }
        : fromArg || selectedApplicant;
    if (!target) return;

    const recipientUserId = target.applicantUserId ?? undefined;
    const recipientEmail = target.email || undefined;
    if (!recipientUserId && !recipientEmail) {
      toast.error('Unable to create offer. Applicant contact details are missing.');
      return;
    }
    if (!target.application_uuid) {
      toast.error('Unable to create offer without a gig application.');
      return;
    }

    navigate({
      to: '/offers/create',
      search: {
        applicationUuid: target.application_uuid,
        recipientEmail,
        recipientName: target.fullName || target.username || undefined,
        recipientUserId,
        title: target.appliedToGig || undefined,
        description: target.gigDescription || undefined,
        gigUuid: target.gigUuid || undefined,
      },
    });
  };

  return (
    <div>
      {/* Sub-filters bar (normal flow, non-sticky) */}
      <ApplicantsFilters
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        counts={counts}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        gigFilter={gigFilter}
        onGigFilterChange={setGigFilter}
        availableGigs={availableGigs}
      />

      {/* Main Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Applicants List */}
        <section className="space-y-3 lg:col-span-6">
          {listLoading && (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          )}

          {listError && !listLoading && (
            <div className="rounded-2xl border border-white/10 bg-[#0A0C11] p-8 text-center text-sm text-zinc-400">
              Failed to load applications.
            </div>
          )}

          {!listLoading && !listError && (
            <>
              {filteredApplicants.length === 0 ? (
                <EmptyState
                  size="compact"
                  icon={<FiFilter size={18} />}
                  title="No applicants found"
                  description="No applicants match your filters. Try a different status or gig."
                  className="rounded-2xl border border-white/10 bg-[#0A0C11]"
                />
              ) : (
                filteredApplicants.map((app) => (
                  <ApplicantCard
                    key={app.application_uuid}
                    applicant={app}
                    isSelected={app.application_uuid === selectedUuid}
                    onSelect={() => {
                      setSelectedUuid(app.application_uuid);
                      setShowMobileSheet(true);
                    }}
                  />
                ))
              )}
            </>
          )}
        </section>

        {/* Right Column: Sticky Applicant Detail Panel Content (Desktop) */}
        <aside className="hidden lg:block lg:col-span-6">
          <div
            ref={detailPanelRef}
            style={{ height: detailPanelHeight }}
            className="sticky top-[calc(var(--header-height,5.5rem)+1.25rem)] flex h-[calc(100dvh-var(--header-height,5.5rem)-3rem)] flex-col min-h-0"
          >
            {detailLoading || profileLoading ? (
              <div className="h-full rounded-3xl border border-white/10 bg-[#0A0C11] p-6 animate-pulse space-y-4">
                <div className="h-12 w-12 rounded-full bg-white/5" />
                <div className="h-6 w-48 rounded bg-white/5" />
                <div className="h-10 rounded-2xl bg-white/5" />
                <div className="h-32 rounded-2xl bg-white/5" />
              </div>
            ) : (
              <ApplicantDetailPanel
                applicant={selectedApplicant}
                onPickFirst={
                  filteredApplicants.length > 0
                    ? () => setSelectedUuid(filteredApplicants[0].application_uuid)
                    : undefined
                }
                onAccept={() => setShowAcceptModal(true)}
                onReject={() => setShowRejectModal(true)}
                onCreateOffer={() => openCreateOffer(selectedApplicant)}
                existingOfferId={existingOfferId}
              />
            )}
          </div>
        </aside>
      </div>

      {/* Mobile Bottom Sheet Drawer (< lg) */}
      <AnimatePresence>
        {showMobileSheet && selectedUuid && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm lg:hidden"
            onClick={() => setShowMobileSheet(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-0 left-0 right-0 flex h-[88dvh] max-h-[88dvh] flex-col rounded-t-3xl border-t border-white/15 bg-[#0A0C11] p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-2xl overflow-hidden min-h-0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between border-b border-white/10 pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-10 rounded-full bg-zinc-500" />
                  <span className="text-sm font-bold text-white">Applicant Details</span>
                </div>
                <button
                  onClick={() => setShowMobileSheet(false)}
                  className="rounded-full p-2 text-zinc-400 hover:bg-white/10 hover:text-white"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
                <ApplicantDetailPanel
                  applicant={selectedApplicant}
                  onPickFirst={
                    filteredApplicants.length > 0
                      ? () => setSelectedUuid(filteredApplicants[0].application_uuid)
                      : undefined
                  }
                  onAccept={() => setShowAcceptModal(true)}
                  onReject={() => setShowRejectModal(true)}
                  onCreateOffer={() => openCreateOffer(selectedApplicant)}
                  existingOfferId={existingOfferId}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Modals */}
      {showAcceptModal && selectedApplicant && (
        <AcceptApplicationModal
          applicantName={selectedApplicant.fullName || selectedApplicant.username}
          onConfirm={handleAcceptConfirm}
          onCancel={() => setShowAcceptModal(false)}
        />
      )}

      {showRejectModal && selectedApplicant && (
        <RejectApplicationModal
          applicantName={selectedApplicant.fullName || selectedApplicant.username}
          onConfirm={handleRejectConfirm}
          onCancel={() => setShowRejectModal(false)}
        />
      )}
    </div>
  );
};
